import { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFields } from '../../../hooks/useFields';
import { useImpactAggregationProperties } from '../../../hooks/ideaPage/useIdeaQueries';
import {
  EXPLORE_IDEAS_LIST_PAGE_SIZE,
  fetchExploreIdeasListPageRawData,
  getExploreIdeasListPageSlice,
  mapExploreIdeasListPageData,
  type ExploreIdeaListRowData,
} from './explore-ideas-list-data';
import {
  buildExploreIdeasListSignature,
  exploreIdeasListKeys,
} from './exploreIdeasListKeys';

export { EXPLORE_IDEAS_LIST_PAGE_SIZE };
export type { ExploreIdeaListRowData, ExploreIdeaImpactMetric } from './explore-ideas-list-data';

export function useExploreIdeasListPage(ideaIds: string[], page: number) {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();
  const { ideaStatuses } = useFields();
  const { data: impactConfig, isSuccess: impactPropsReady } = useImpactAggregationProperties();

  const impactProperties = useMemo(() => {
    const rawProps =
      (impactConfig as { impactAggregationProperties?: unknown[] })?.impactAggregationProperties ??
      impactConfig ??
      [];
    return Array.isArray(rawProps) ? rawProps : [];
  }, [impactConfig]);

  const listSignature = useMemo(() => buildExploreIdeasListSignature(ideaIds), [ideaIds]);
  const pageIdeaIds = useMemo(
    () => getExploreIdeasListPageSlice(ideaIds, page),
    [ideaIds, page]
  );
  const pageStartIndex = (page - 1) * EXPLORE_IDEAS_LIST_PAGE_SIZE;
  const pageEndIndex = Math.min(pageStartIndex + EXPLORE_IDEAS_LIST_PAGE_SIZE, ideaIds.length);
  const totalPages = Math.max(1, Math.ceil(ideaIds.length / EXPLORE_IDEAS_LIST_PAGE_SIZE));

  const pageQuery = useQuery({
    queryKey: exploreIdeasListKeys.page(listSignature, page),
    queryFn: () =>
      fetchExploreIdeasListPageRawData(pageIdeaIds, auth0, queryClient),
    enabled: pageIdeaIds.length > 0 && impactPropsReady,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => {
    if (!pageQuery.data) {
      return [] as ExploreIdeaListRowData[];
    }
    return mapExploreIdeasListPageData(
      pageQuery.data,
      ideaStatuses ?? [],
      impactProperties
    ).rows;
  }, [pageQuery.data, ideaStatuses, impactProperties]);

  const isWaitingForImpactConfig = pageIdeaIds.length > 0 && !impactPropsReady;
  const isShowingPlaceholderRows =
    pageQuery.isFetching && pageQuery.isPlaceholderData;
  const isPageLoading =
    isWaitingForImpactConfig || pageQuery.isLoading || isShowingPlaceholderRows;

  return {
    rows,
    isPageLoading,
    isPageFetching: pageQuery.isFetching,
    totalPages,
    pageStartIndex,
    pageEndIndex,
  };
}
